/*
 * CostPerLeadComparison
 * Side-by-side cost per lead and cost per signed client for paid ads vs SEO,
 * using only numbers the reader enters for the same period.
 *
 * Rules:
 * - Inputs per channel: spend (>= 0, decimals allowed), leads and signed clients (whole numbers >= 0).
 * - A channel is calculated once all three of its fields are filled in and valid.
 * - Signed clients may not exceed leads; otherwise that channel shows an error and no figures.
 * - Cost per lead = spend / leads; cost per client = spend / clients. A zero denominator shows "n/a".
 * - When both channels have a cost per client, the lower one is named; equal values are called equal.
 *   If either channel has no clients, no winner is named.
 * - SEO note: SEO spend early on pays for results that arrive later, so the tool always reminds the
 *   reader to compare over several months. No benchmarks or defaults are used.
 * No tracking, storage or network calls.
 */
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";

type Channel = "ppc" | "seo";
type Field = "spend" | "leads" | "clients";

const CHANNELS: { key: Channel; name: string; hint: string }[] = [
  { key: "ppc", name: "Paid ads (PPC)", hint: "Google Ads, LSA, Bing" },
  { key: "seo", name: "SEO", hint: "Agency fee or in-house cost" },
];

const FIELDS: { key: Field; label: string; placeholder: string; decimals: boolean }[] = [
  { key: "spend", label: "Spend for the period ($)", placeholder: "e.g. 6000", decimals: true },
  { key: "leads", label: "Leads (calls + forms)", placeholder: "e.g. 40", decimals: false },
  { key: "clients", label: "Signed clients", placeholder: "e.g. 5", decimals: false },
];

type Result =
  | { state: "empty" }
  | { state: "invalid"; message: string }
  | { state: "ok"; cpl: number | null; cpc: number | null };

function money(n: number | null): string {
  if (n === null) return "n/a";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function calc(v: Record<Field, string>): Result {
  if (Object.values(v).some((x) => x.trim() === "")) return { state: "empty" };
  const spend = Number(v.spend);
  const leads = Number(v.leads);
  const clients = Number(v.clients);
  if (!Number.isFinite(spend) || spend < 0) return { state: "invalid", message: "Spend must be zero or more." };
  if (!Number.isInteger(leads) || leads < 0 || !Number.isInteger(clients) || clients < 0)
    return { state: "invalid", message: "Leads and clients must be whole numbers of zero or more." };
  if (clients > leads) return { state: "invalid", message: "Signed clients can't be higher than leads." };
  return {
    state: "ok",
    cpl: leads > 0 ? spend / leads : null,
    cpc: clients > 0 ? spend / clients : null,
  };
}

const EMPTY: Record<Field, string> = { spend: "", leads: "", clients: "" };

export default function CostPerLeadComparison() {
  const baseId = useId();
  const [values, setValues] = useState<Record<Channel, Record<Field, string>>>({
    ppc: { ...EMPTY },
    seo: { ...EMPTY },
  });

  const results: Record<Channel, Result> = { ppc: calc(values.ppc), seo: calc(values.seo) };
  const p = results.ppc;
  const s = results.seo;

  let verdict: { text: string; href: string; linkText: string } | null = null;
  if (p.state === "ok" && s.state === "ok" && p.cpc !== null && s.cpc !== null) {
    if (Math.round(p.cpc) < Math.round(s.cpc)) {
      verdict = {
        text: "Paid ads are cheaper per signed client in this period. That's common while SEO is still building, so keep ads running and check again in a few months.",
        href: "/services/ppc-for-law-firms",
        linkText: "PPC for law firms",
      };
    } else if (Math.round(s.cpc) < Math.round(p.cpc)) {
      verdict = {
        text: "SEO is cheaper per signed client in this period. Look at which pages bring those clients and build more like them.",
        href: "/services/law-firm-seo",
        linkText: "Law firm SEO",
      };
    } else {
      verdict = {
        text: "Both channels cost about the same per signed client in this period.",
        href: "/pricing",
        linkText: "Compare SEO pricing",
      };
    }
  }

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Paid ads vs SEO: your cost per client</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter your own figures for the same period (for example, the last 6 months). Nothing is saved or sent.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {CHANNELS.map((c) => (
          <fieldset key={c.key} className="rounded-lg border border-border p-4">
            <legend className="px-1 text-sm font-semibold">{c.name}</legend>
            <p className="text-xs text-muted-foreground">{c.hint}</p>
            <div className="mt-3 space-y-3">
              {FIELDS.map((f) => {
                const id = `${baseId}-${c.key}-${f.key}`;
                return (
                  <div key={f.key}>
                    <label htmlFor={id} className="block text-sm font-medium">
                      {f.label}
                    </label>
                    <input
                      id={id}
                      type="number"
                      inputMode={f.decimals ? "decimal" : "numeric"}
                      min={0}
                      step={f.decimals ? "any" : 1}
                      value={values[c.key][f.key]}
                      onChange={(e) =>
                        setValues((prev) => ({ ...prev, [c.key]: { ...prev[c.key], [f.key]: e.target.value } }))
                      }
                      placeholder={f.placeholder}
                      className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
                    />
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        <div className="grid gap-3 md:grid-cols-2">
          {CHANNELS.map((c) => {
            const r = results[c.key];
            return (
              <div key={c.key} className="rounded-md border border-border bg-card p-3 text-sm">
                <p className="font-semibold">{c.name}</p>
                {r.state === "empty" && <p className="mt-1 text-muted-foreground">Fill in all three fields.</p>}
                {r.state === "invalid" && <p className="mt-1">{r.message}</p>}
                {r.state === "ok" && (
                  <dl className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <dt className="text-xs text-muted-foreground">Cost per lead</dt>
                      <dd className="text-lg font-semibold">{money(r.cpl)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Cost per client</dt>
                      <dd className="text-lg font-semibold">{money(r.cpc)}</dd>
                    </div>
                  </dl>
                )}
              </div>
            );
          })}
        </div>

        {verdict && (
          <p className="mt-4 text-sm">
            {verdict.text}{" "}
            <Link to={verdict.href} className={`font-medium underline underline-offset-4 ${focusRing}`}>
              {verdict.linkText} →
            </Link>
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          SEO spend in the early months pays for results that arrive later, so a short window makes SEO look more
          expensive than it is. Compare over several months, and see{" "}
          <Link to="/pricing" className={`underline underline-offset-4 ${focusRing}`}>
            our pricing
          </Link>{" "}
          for how SEO scope is set.
        </p>
      </div>
    </section>
  );
}
